import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  FormListFieldData,
  Image,
  Row,
  Space,
  Switch,
} from "antd";
import { useEffect } from "react";
import DeleteIcon from "../../../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../../../assets/icons/EditIcon";
import bkash from "../../../../../../assets/images/bKash.svg";
import nagad from "../../../../../../assets/images/nagad.svg";
import rocket from "../../../../../../assets/images/roket.svg";
import sheba_pay from "../../../../../../assets/images/sheba-pay.svg";
import ConfirmModal from "../../../../../../components/ui/ConfirmModal";
import { useDeletePaymentInfoMutation } from "../../../../../../features/Vendor/vendor.api";
import useError from "../../../../../../hooks/useError";
import useModal from "../../../../../../hooks/useModal";
import { toast } from "../../../../../../lib/toast";
import { ModalData, ModalType } from "../Payments";

type Props = {
  onRemove: () => void;
  field: { key: number; name: number; fieldKey?: number | undefined };
  handleOpen: ({ type, data }: { data: ModalData; type: ModalType }) => void;
  data: any;
  fields: FormListFieldData[];
  form: FormInstance<any>;
};

const IMAGE_CONFIG = {
  shebapay: sheba_pay,
  bkash: bkash,
  rocket: rocket,
  nagad: nagad,
};

export default function MFSCard({
  onRemove,
  field,
  handleOpen,
  data,
  fields,
  form,
}: Props) {
  const {
    open,
    handleOpen: hOpen,
    handleClose,
  } = useModal<unknown, "REMOVE-MFS">();
  const [deleteBank, { isLoading, isSuccess, isError, error }] =
    useDeletePaymentInfoMutation();

  useEffect(() => {
    if (isSuccess) {
      onRemove && onRemove();
      toast("success", "MFS account deleted successfully.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useError(isError, error, "MFS delete failed.");

  if (!data?.mfsAccount) return null;
  const { mfsAccount } = data || {};

  const type = (mfsAccount?.mfs_type as string)?.toLowerCase();
  const logo = IMAGE_CONFIG[type as keyof typeof IMAGE_CONFIG];

  return (
    <Card className="shadow-sm rounded-xl !w-full border-none bg-theme-slate-25">
      <Row gutter={[16, 16]}>
        {/* Logo */}
        <Col span={24} style={{ textAlign: "left" }}>
          <Flex justify="space-between" align="start">
            <Image
              src={mfsAccount?.mfs_logo || logo}
              alt="City Bank"
              height={80}
              preview={false}
            />
            <Space>
              <Button
                onClick={() =>
                  handleOpen({
                    type: "UPDATE-MFS",
                    data: { index: field.name },
                  })
                }
                className="border-none bg-transparent shadow-none size-8 p-0 hover:!bg-transparent"
              >
                <EditIcon height={24} width={24} />
              </Button>
              <Button
                onClick={() => hOpen({ type: "REMOVE-MFS" })}
                className="border-none bg-transparent shadow-none size-8 p-0 hover:!bg-transparent"
              >
                <DeleteIcon height={24} width={24} />
              </Button>
            </Space>
          </Flex>
        </Col>

        {/* Left column */}
        <Col span={24}>
          <p className="text-theme-gray-500 font-medium text-sm">
            Account Name
          </p>
          <p className="text-theme-gray-900 font-medium text-sm mb-1.5">
            {mfsAccount?.account_name}
          </p>

          <p className="text-theme-gray-500 font-medium text-sm">
            Account Number
          </p>
          <p className="text-theme-gray-900 font-medium text-sm">
            {mfsAccount?.account_number}
          </p>
        </Col>
      </Row>
      {/* Switch at bottom */}
      <Flex align="center" gap={8}>
        <Form.Item
          className="mb-0"
          name={[field.name, "is_default"]}
          valuePropName="checked"
        >
          <Switch
            onChange={(checked) => {
              if (checked) {
                fields.forEach((f, i) => {
                  if (i !== field.name) {
                    form.setFieldValue(["paymentInfo", i, "is_default"], false);
                  }
                });
              }
            }}
          />
        </Form.Item>
        <span className="text-theme-gray-24 font-medium">Set as Default</span>
      </Flex>

      <ConfirmModal
        closable
        icon="DELETE"
        okText={"Yes, Delete"}
        confirmTitle="Remove MFS Account"
        questionText={`Are you sure you want to delete bank account of “${mfsAccount?.account_name}”`}
        open={open.open}
        handleClose={handleClose}
        okLoading={isLoading}
        handleOk={() => {
          data?.vendor_id
            ? deleteBank({ vendorId: data?.vendor_id, paymentInfoId: data?.id })
            : onRemove();
        }}
      />
    </Card>
  );
}
